import React, { useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from 'react-query';
import { useSelector } from 'react-redux';
import { useAuthUser, useAuthTenant } from 'store/auth';
import styled from 'styled-components';
import { AttachFileIcon, PdfIcon, DocIcon } from 'Components/common/Icons';
import { Main as Container } from 'Components/Main';
import { ButtonForm } from 'Components/Form/styles';
import {
  editPolicyVersionStatus,
  getPolicyById,
  getPolicyAcceptsSummary,
  sendPolicyUserAcceptRequest,
  getPolicyComments,
  addPolicyComment,
  rejectPolicyVersionWithEmail,
  regenerateAttachmentDownloadUrl,
} from 'services/policies-service';
import { getBrandingForDisplay } from 'services/company-service';
import { apiClient } from 'services/api-client';
import { formatDateTimeToDateTimeBR, formatDateToDateBRPolish, formatDateLongWithTime } from 'utils/date_utils';
import { Dialog, TextField, PageHeader, Button, PlusSvg } from 'Components/ui';
import 'react-quill/dist/quill.snow.css';
import { useReactToPrint } from 'react-to-print';
import * as pdfjsLib from 'pdfjs-dist/build/pdf';
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.entry';
import { useTranslation } from 'react-i18next';
import { isVoTenant } from 'utils/tenant_utils';
import { POLICY_VERSION_STATUS, policyWorkflowErrorMessage } from 'utils/policy_version_utils';
import { PageLoaderShow } from 'Components/PageLoader/Show';

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

// ── Layout ──────────────────────────────────────────────────────────
const PageWrap = styled.div`
  padding: 24px 0 32px;
`;
const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
`;
const OutlineBtn = styled.button`
  height: 40px;
  background: white;
  color: var(--vo-slate);
  border: 1.5px solid var(--vo-line);
  border-radius: 8px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 0 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  &:hover { background: var(--vo-mist); }
`;
const PrimaryBtn = styled.button`
  height: 40px;
  background: var(--color-brand);
  color: white;
  border: none;
  border-radius: 8px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 0 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  &:hover { background: var(--color-brand-strong); }
`;

// ── Base Card ────────────────────────────────────────────────────────
const Card = styled.div`
  background: white;
  border-radius: 14px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
  margin-bottom: 16px;
`;

// ── Info Card ────────────────────────────────────────────────────────
const InfoCard = styled(Card)`
  padding: 20px 24px;
`;
const InfoHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;
const GradientIcon = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: linear-gradient(135deg, var(--vo-cobalto), var(--vo-capri));
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;
const InfoContent = styled.div`
  flex: 1;
`;
const InfoNameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 6px;
`;
const PolicyName = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 18px;
  font-weight: 700;
  color: var(--vo-ink);
  margin: 0;
  letter-spacing: -0.01em;
`;
const VersionBadge = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 700;
  color: var(--color-brand);
  background: var(--vo-capri-50);
  padding: 2px 10px;
  border-radius: 20px;
`;
const MetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
`;
const MetaItem = styled.span`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: var(--vo-slate);
  strong { color: var(--vo-ink-2); font-weight: 600; }
`;

// ── Pipeline Card ─────────────────────────────────────────────────────
const PipelineCard = styled(Card)`
  padding: 24px;
`;
const PipelineHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 20px;
`;
const PipelineTitle = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: var(--vo-ink);
  margin: 0 0 2px;
`;
const PipelineSubtitle = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  color: var(--vo-slate);
  margin: 0;
`;
const StatusBadge = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 700;
  color: ${({ $color }) => $color};
  background: ${({ $bg }) => $bg};
  padding: 4px 12px;
  border-radius: 20px;
  white-space: nowrap;
`;
const PipelineGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 20px 1fr 20px 1fr 20px 1fr;
  align-items: start;
  gap: 0;
`;
const PipelineArrow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding-top: 16px;
`;
const StepBox = styled.div`
  background: ${({ $done, $active }) => $done ? 'color-mix(in srgb, var(--vo-success) 5%, transparent)' : $active ? 'color-mix(in srgb, var(--vo-info) 6%, transparent)' : 'var(--vo-mist)'};
  border: 1.5px solid ${({ $done, $active }) => $done ? 'var(--vo-success-20)' : $active ? 'color-mix(in srgb, var(--vo-info) 20%, transparent)' : 'var(--vo-cloud)'};
  border-radius: 10px;
  padding: 14px;
`;
const StepTop = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 10px;
`;
const StepIconBox = styled.div`
  width: 34px;
  height: 34px;
  border-radius: 8px;
  background: ${({ $done, $active }) => $done ? 'color-mix(in srgb, var(--vo-success) 12%, transparent)' : $active ? 'linear-gradient(135deg,var(--vo-cobalto),var(--vo-capri))' : 'var(--vo-mist)'};
  border: ${({ $done, $active }) => (!$done && !$active) ? '1.5px solid var(--vo-line)' : 'none'};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;
const StepLabel = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 700;
  color: var(--vo-slate);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin: 0 0 2px;
`;
const StepName = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: var(--vo-ink);
  font-weight: 500;
  margin: 0;
`;
const StepDate = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 11px;
  color: ${({ $done }) => $done ? 'var(--vo-success)' : 'var(--vo-steel)'};
  margin: 2px 0 0;
`;
const StepNote = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  color: var(--vo-slate);
  margin: 0 0 8px;
`;
const StepBtns = styled.div`
  display: flex;
  gap: 6px;
`;
const StepBtn = styled.button`
  height: 32px;
  background: ${({ $danger, $green }) => $danger ? 'var(--vo-danger)' : $green ? 'var(--vo-success)' : 'var(--color-brand)'};
  color: white;
  border: none;
  border-radius: 8px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  padding: 0 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  flex: 1;
  &:hover { opacity: 0.88; }
`;

// ── Accepts Card ──────────────────────────────────────────────────────
const AcceptsCard = styled(Card)`
  padding: 20px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;
const AcceptsLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`;
const AcceptsIconBox = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: var(--vo-info-10);
  display: flex;
  align-items: center;
  justify-content: center;
`;
const AcceptsNum = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 24px;
  font-weight: 700;
  color: var(--color-brand);
  margin: 0;
  line-height: 1;
`;
const AcceptsUnit = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: var(--vo-slate);
`;
const AcceptsLabel = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  color: var(--vo-slate);
  margin: 0;
`;

// ── Document Card ─────────────────────────────────────────────────────
const DocCard = styled(Card)``;
const DocHeader = styled.div`
  padding: 16px 24px;
  border-bottom: 1px solid var(--vo-cloud);
  display: flex;
  align-items: center;
  justify-content: space-between;
`;
const DocTitle = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: var(--vo-ink);
  margin: 0;
`;
const DocBtns = styled.div`
  display: flex;
  gap: 8px;
`;
const SmallOutlineBtn = styled.button`
  height: 32px;
  background: white;
  color: var(--vo-slate);
  border: 1.5px solid var(--vo-line);
  border-radius: 8px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  cursor: pointer;
  padding: 0 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  &:hover { background: var(--vo-mist); }
`;
const DocContent = styled.div`
  padding: 32px 48px;
  min-height: 240px;
  background: var(--vo-mist);
`;
const DocPaper = styled.div`
  max-width: 720px;
  margin: 0 auto;
  background: white;
  border-radius: 4px;
  padding: 40px 48px;
  box-shadow: 0 2px 12px color-mix(in srgb, var(--vo-escuro) 10%, transparent);
  min-height: 240px;
`;
const DocPaperTitle = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 20px;
  font-weight: 700;
  color: var(--vo-ink);
  text-align: center;
  margin: 0 0 8px;
`;
const DocPaperSub = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  color: var(--vo-slate);
  text-align: center;
  margin: 0 0 28px;
  padding-bottom: 20px;
  border-bottom: 1px solid var(--vo-cloud);
`;
const AttachFileRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 24px;
  border-bottom: 1px solid var(--vo-mist);
`;
const AttachFileName = styled.p`
  margin: 0;
  font-family: 'Lato', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: var(--vo-ink);
`;
const AttachFileMeta = styled.p`
  margin: 0;
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  color: var(--vo-slate);
`;

// ── Comments Card ─────────────────────────────────────────────────────
const CommentsCard = styled(Card)`
  overflow: hidden;
`;
const CommentsToggle = styled.div`
  padding: 16px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  border-bottom: ${({ $open }) => $open ? '1px solid var(--vo-cloud)' : 'none'};
  &:hover { background: var(--vo-capri-25); }
`;
const CommentsToggleLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;
const CommentsTitleText = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: var(--vo-ink);
  margin: 0;
`;
const CommentsCount = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 700;
  color: var(--color-brand);
  background: var(--vo-capri-50);
  padding: 2px 8px;
  border-radius: 10px;
`;
const ChevronIcon = styled.svg`
  transform: ${({ $open }) => $open ? 'rotate(180deg)' : 'rotate(0deg)'};
  transition: transform 0.2s;
  flex-shrink: 0;
`;
const CommentsBody = styled.div`
  padding: 0 24px 8px;
`;
const CommentItem = styled.div`
  padding: 16px 0;
  border-bottom: 1px solid var(--vo-mist);
  display: flex;
  align-items: flex-start;
  gap: 12px;
  &:last-child { border-bottom: none; }
`;
const CommentIconBox = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: ${({ $bg }) => $bg};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;
const CommentContent = styled.div`
  flex: 1;
`;
const CommentMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
  flex-wrap: wrap;
`;
const CommentAuthor = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: var(--vo-ink);
`;
const CommentBadge = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: ${({ $color }) => $color};
  background: ${({ $bg }) => $bg};
  padding: 2px 8px;
  border-radius: 10px;
`;
const CommentDate = styled.span`
  font-family: 'Lato', sans-serif;
  font-size: 11px;
  color: var(--vo-steel);
`;
const CommentText = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: var(--vo-ink-2);
  margin: 0;
  line-height: 1.55;
`;
const CommentsFooter = styled.div`
  padding: 12px 24px;
  border-top: 1px solid var(--vo-mist);
`;
const AddCommentBtn = styled.button`
  height: 36px;
  background: var(--color-brand);
  color: white;
  border: none;
  border-radius: 8px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  padding: 0 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  &:hover { background: var(--color-brand-strong); }
`;
const EmptyNote = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: var(--vo-slate);
  text-align: center;
  padding: 20px 0;
  margin: 0;
`;

// ── Helpers ───────────────────────────────────────────────────────────
const getVersionStatusBadge = (status) => {
  switch (status) {
    case 'Publicado': return { color: 'var(--vo-success)', bg: 'var(--vo-success-10)' };
    case 'Aprovado':  return { color: 'var(--vo-celeste)', bg: 'var(--vo-info-10)' };
    case 'Revisado':  return { color: 'var(--vo-celeste)', bg: 'var(--vo-info-10)' };
    case 'Concluído': return { color: 'var(--vo-warning)', bg: 'var(--vo-warning-10)' };
    default:          return { color: 'var(--vo-slate)', bg: 'var(--vo-slate-10)' };
  }
};

const getCommentStyle = (comment) => {
  if (comment.type === 'rejection') return {
    iconBg: 'color-mix(in srgb, var(--vo-danger) 12%, transparent)', iconColor: 'var(--vo-danger)',
    badgeColor: 'var(--vo-danger)', badgeBg: 'var(--vo-danger-10)', icon: 'x',
  };
  const roles = {
    'Autor':     { iconBg: 'var(--vo-success-10)',  iconColor: 'var(--vo-success)', badgeColor: 'var(--vo-success)', badgeBg: 'var(--vo-success-10)',  icon: 'check' },
    'Revisor':   { iconBg: 'var(--vo-warning-10)',   iconColor: 'var(--vo-warning)', badgeColor: 'var(--vo-warning)', badgeBg: 'var(--vo-warning-10)',   icon: 'chat'  },
    'Aprovador': { iconBg: 'var(--vo-info-10)',  iconColor: 'var(--vo-celeste)', badgeColor: 'var(--vo-celeste)', badgeBg: 'var(--vo-capri-50)',              icon: 'chat'  },
  };
  return { ...(roles[comment.authorRole] || roles['Revisor']), icon: 'chat' };
};

// ── Inline SVGs ───────────────────────────────────────────────────────
const IconDoc = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
    <polyline points="14 2 14 8 20 8"/>
  </svg>
);
const IconCheck = ({ color = 'white', size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);
const IconX = ({ color = 'white', size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);
const IconArrow = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--vo-line)" strokeWidth="2">
    <polyline points="9 18 15 12 9 6"/>
  </svg>
);
const IconUsers = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--color-brand)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
    <circle cx="9" cy="7" r="4"/>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
  </svg>
);
const IconDownload = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
    <polyline points="7 10 12 15 17 10"/>
    <line x1="12" y1="15" x2="12" y2="3"/>
  </svg>
);
const IconPrint = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 6 2 18 2 18 9"/>
    <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
    <rect x="6" y="14" width="12" height="8"/>
  </svg>
);
const IconChat = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--color-brand)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
  </svg>
);
const IconPerson = ({ color = 'white', size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
    <circle cx="12" cy="7" r="4"/>
  </svg>
);
const IconPublish = ({ color = 'white', size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2L2 7l10 5 10-5-10-5z"/>
    <path d="M2 17l10 5 10-5"/>
  </svg>
);

// ═══════════════════════════════════════════════════════════════════
export const PolicyVersionShow = () => {
  const { t } = useTranslation(['policy']);
  const { id, idVersion } = useParams();
  const user = useSelector(useAuthUser);
  const tenant = useSelector(useAuthTenant);
  const componentRef = useRef();
  const combinedRef = useRef();
  const navigate = useNavigate();

  const [refetch, setRefetch] = useState(0);
  const [showCombinedContent, setShowCombinedContent] = useState(false);
  const [pdfPages, setPdfPages] = useState([]);
  const [showTranscriptionModal, setShowTranscriptionModal] = useState(false);
  const [transcribedText, setTranscribedText] = useState('');
  const [comments, setComments] = useState([]);
  const [commentsExpanded, setCommentsExpanded] = useState(true);
  const [currentAttachmentUrl, setCurrentAttachmentUrl] = useState(null);

  const { isLoading, data: policy } = useQuery(['getPolicyById', refetch], async () => {
    const res = await getPolicyById(id);
    return res.status === 200 ? res.data : null;
  });

  const { data: acceptsSummary } = useQuery(['getPolicyAcceptsSummary', refetch, id, idVersion], async () => {
    const res = await getPolicyAcceptsSummary(id, idVersion);
    return res.status === 200 ? res.data : { sent: 0, accepted: 0 };
  });
  // Requests e-mailed for this version (one acceptance record each) — drives View vs Send below.
  const policyAccepts = acceptsSummary?.sent;

  // Cover page logo: sourced from Custom Branding (Settings > Branding), same as
  // Components/Layout/Authenticated/index.jsx and SideBar — not the Company Profile logo.
  // No fallback to the default VO logo for a regular tenant: this document is exported/
  // shared as the tenant's own artifact, so absent a configured branding logo the cover
  // page shows the tenant name as text instead (never both together — see the two
  // brandingLogoUrl ? <img/> : <h1/> cover blocks below). VO's own tenant is the one
  // exception — it always gets its real logo here (light-background variant, matching
  // this page's white background), never falling through to the text-only name state,
  // regardless of whether the 'vo' company record has branding configured.
  const { data: brandingData } = useQuery(
    ['company-branding', tenant?.slug],
    getBrandingForDisplay,
    { staleTime: 5 * 60 * 1000, retry: false, enabled: !!tenant?.slug }
  );
  const brandingLogoUrl = isVoTenant(tenant?.slug)
    ? '/img/logo-horizontal.png'
    : (brandingData?.data?.branding?.logoUrl ?? null);

  const loadExampleComments = () => {
    setComments([
      { id: 1, content: 'Esta política precisa de ajustes na seção de segurança', type: 'comment', authorName: 'João Silva', authorRole: 'Revisor', createdAt: '2025-01-15T10:30:00.000Z' },
      { id: 2, content: 'Documento rejeitado por não atender aos critérios de compliance', type: 'rejection', authorName: 'Maria Santos', authorRole: 'Aprovador', createdAt: '2025-01-14T14:20:00.000Z' },
      { id: 3, content: 'Excelente trabalho na documentação dos processos', type: 'comment', authorName: 'Pedro Costa', authorRole: 'Autor', createdAt: '2025-01-13T09:15:00.000Z' },
    ]);
  };

  useQuery(['getPolicyComments', id, idVersion, refetch], async () => {
    try {
      const res = await getPolicyComments(id, idVersion);
      if (res.status === 200) { setComments(res.data || []); return res.data; }
    } catch { loadExampleComments(); }
    return [];
  });

  const mutation = useMutation(() => sendPolicyUserAcceptRequest(id, idVersion), {
    onError: err => Dialog.alert({ title: 'Erro!', message: err.response?.data?.message ?? err.message, icon: 'error' }),
    onSuccess: data => { if (data.status === 200) setRefetch(r => r + 1); },
  });

  // ── Permission helpers ────────────────────────────────────────────
  const canAddComment = () => {
    if (!policy?.policyVersions?.[parseInt(idVersion)] || !user) return false;
    const ver = policy.policyVersions[parseInt(idVersion)];
    const uid = user.userId;
    return [ver.author, ver.reviewer, ver.approver, ver.publisher].some(p => p && (p._id === uid || p.id === uid));
  };

  const canViewApprovalHistory = () => {
    if (!policy?.policyVersions?.[parseInt(idVersion)] || !user) return false;
    const ver = policy.policyVersions[parseInt(idVersion)];
    const uid = user.userId;
    return [ver.author, ver.reviewer, ver.approver].some(p => p && (p._id === uid || p.id === uid));
  };

  const canExportPDF = () => {
    if (!policy?.policyVersions?.[parseInt(idVersion)] || !user) return false;
    const ver = policy.policyVersions[parseInt(idVersion)];
    const uid = user.userId;
    if (user.roleActive !== 'user_default') return true;
    return [ver.reviewer, ver.approver, ver.publisher].some(p => p && (p._id === uid || p.id === uid));
  };

  // ── Status actions ────────────────────────────────────────────────
  const updateStatus = async (status) => {
    try {
      Dialog.loading({ title: t('policy:versionShow.loading') });
      await editPolicyVersionStatus(id, idVersion, status);
      Dialog.close();
      Dialog.fire({
        title: t('policy:versionShow.swal.successTitle'),
        text: t('policy:versionShow.swal.statusSuccess'),
        icon: 'success',
        willClose: () => setRefetch(r => r + 1),
      });
    } catch (err) {
      Dialog.alert({ title: t('policy:versionShow.swal.errorTitle'), message: policyWorkflowErrorMessage(t, err), icon: 'error' });
    }
  };

  const handleReject = async (tipo) => {
    const getTitleByType = (t2) => {
      if (t2 === 'revisor') return t('policy:versionShow.rejectTypes.reviewer');
      if (t2 === 'aprovador') return t('policy:versionShow.rejectTypes.approver');
      if (t2 === 'publicador') return t('policy:versionShow.rejectTypes.publisher');
      return t('policy:versionShow.rejectTypes.document');
    };
    const { value: motivo } = await Dialog.fire({
      title: t('policy:versionShow.swal.rejectTitle', { type: getTitleByType(tipo) }),
      html: `<div style="text-align:left;margin-bottom:15px;"><p>${t('policy:versionShow.swal.rejectInfo')}</p><p><strong>${t('policy:versionShow.swal.rejectReason')}</strong></p></div>`,
      input: 'textarea',
      inputPlaceholder: t('policy:versionShow.swal.rejectPlaceholder'),
      inputAttributes: { 'aria-label': t('policy:versionShow.swal.rejectAriaLabel'), style: 'min-height:100px;resize:vertical;' },
      showCancelButton: true,
      confirmButtonText: t('policy:versionShow.swal.rejectConfirm'),
      cancelButtonText: t('policy:versionShow.swal.rejectCancel'),
      confirmButtonColor: 'var(--vo-danger)',
      cancelButtonColor: 'var(--vo-slate)',
      inputValidator: (v) => (!v || v.trim().length < 10) ? t('policy:versionShow.swal.rejectMinChars') : null,
    });
    if (motivo) {
      try {
        Dialog.loading({ title: t('policy:versionShow.loading') });
        // A rejection always returns the version to draft. The backend derives who rejected (and
        // whether they may) from the session; there is deliberately no fallback to the plain status
        // route — a refused rejection must surface as an error, not be retried another way.
        const rejectionData = { policyId: id, policyVersionId: idVersion, content: `Documento rejeitado: ${motivo.trim()}`, motivo: motivo.trim(), type: 'rejection', authorId: user.userId, authorName: user.name || user.email || 'Usuário', authorRole: tipo === 'revisor' ? 'Revisor' : 'Aprovador', createdAt: new Date().toISOString() };
        await rejectPolicyVersionWithEmail(id, idVersion, { status: POLICY_VERSION_STATUS.STARTED, rejectionReason: motivo.trim() });
        setComments(prev => [{ id: Date.now(), ...rejectionData }, ...prev]);
        Dialog.close();
        Dialog.fire({
          title: t('policy:versionShow.swal.rejectedTitle'),
          html: `<div style="text-align:left;"><p>${t('policy:versionShow.swal.rejectedText')}</p><p><strong>${t('policy:versionShow.swal.rejectedReason')}</strong> ${motivo.trim()}</p><p><em>${t('policy:versionShow.swal.rejectedEmail')}</em></p></div>`,
          icon: 'info',
          confirmButtonText: 'OK',
          willClose: () => { setRefetch(r => r + 1); setTimeout(() => window.location.reload(), 1000); },
        });
      } catch (err) {
        Dialog.close();
        Dialog.alert({ title: t('policy:versionShow.swal.errorTitle'), message: policyWorkflowErrorMessage(t, err) || t('policy:versionShow.swal.rejectError'), icon: 'error' });
      }
    }
  };

  const handleAddComment = async () => {
    const { value: comentario } = await Dialog.fire({
      title: t('policy:versionShow.swal.addCommentTitle'),
      html: `<div style="text-align:left;margin-bottom:15px;"><p>${t('policy:versionShow.swal.addCommentInfo')}</p></div>`,
      input: 'textarea',
      inputPlaceholder: t('policy:versionShow.swal.addCommentPlaceholder'),
      inputAttributes: { 'aria-label': t('policy:versionShow.swal.addCommentAriaLabel'), style: 'min-height:120px;resize:vertical;' },
      showCancelButton: true,
      confirmButtonText: t('policy:versionShow.swal.addCommentConfirm'),
      cancelButtonText: t('policy:versionShow.swal.addCommentCancel'),
      confirmButtonColor: 'var(--color-brand)',
      cancelButtonColor: 'var(--vo-slate)',
      inputValidator: (v) => (!v || v.trim().length < 5) ? t('policy:versionShow.swal.addCommentMinChars') : null,
    });
    if (comentario) {
      try {
        Dialog.loading({ title: t('policy:versionShow.loading') });
        let userRole = 'Usuário';
        if (policy?.policyVersions?.[parseInt(idVersion)]) {
          const ver = policy.policyVersions[parseInt(idVersion)];
          const uid = user.userId;
          if (ver.author && (ver.author._id === uid || ver.author.id === uid)) userRole = 'Autor';
          else if (ver.reviewer && (ver.reviewer._id === uid || ver.reviewer.id === uid)) userRole = 'Revisor';
          else if (ver.approver && (ver.approver._id === uid || ver.approver.id === uid)) userRole = 'Aprovador';
        }
        const commentData = { policyId: id, policyVersionId: idVersion, content: comentario.trim(), type: 'comment', authorId: user.userId, authorName: user.name || user.email || 'Usuário', authorRole: userRole, createdAt: new Date().toISOString() };
        await addPolicyComment(id, idVersion, commentData);
        setComments(prev => [{ id: Date.now(), ...commentData }, ...prev]);
        Dialog.close();
        Dialog.fire({ title: t('policy:versionShow.swal.addCommentSuccess'), text: t('policy:versionShow.swal.addCommentSuccessText'), icon: 'success', confirmButtonText: 'OK', willClose: () => setRefetch(r => r + 1) });
      } catch (err) {
        Dialog.close();
        Dialog.alert({ title: t('policy:versionShow.swal.errorTitle'), message: err?.response?.data?.message || t('policy:versionShow.swal.addCommentError'), icon: 'error' });
      }
    }
  };

  // ── Print ─────────────────────────────────────────────────────────
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: `${policy?.name} - v${policy?.policyVersions?.[idVersion]?.version || ''}`,
    pageStyle: `@page{size:A4;margin:0;}@media print{.no-print{display:none!important;}.document-cover{display:flex!important;flex-direction:column;align-items:center;justify-content:center;height:calc(100vh - 40px);page-break-after:always;text-align:center;padding:5px 40px 40px;border:2px solid var(--color-brand);border-radius:8px;margin:20px;position:relative;box-sizing:border-box;}.document-summary{display:block!important;page-break-after:always;padding:20mm 15mm 15mm;box-sizing:border-box;}.document-body{display:block!important;padding:15mm;box-sizing:border-box;}img,table,tr,td,th,.ql-editor,.no-break{page-break-inside:avoid;break-inside:avoid;}}`,
  });

  const printCombined = useReactToPrint({
    content: () => combinedRef.current,
    documentTitle: `${policy?.name} - v${policy?.policyVersions?.[idVersion]?.version || ''}`,
    pageStyle: `@page{size:A4;margin:12mm;}@media print{html,body{-webkit-print-color-adjust:exact;}img,table,tr,td,th,.ql-editor,.no-break{page-break-inside:avoid;break-inside:avoid;}.page-break{page-break-before:always;break-before:page;}table{border-collapse:collapse;width:100%;}th,td{border:1px solid var(--vo-line);padding:8px;}.ql-editor{font-size:12pt;line-height:1.5;}.pdf-page-img{width:100%;height:auto;}.no-print{display:none!important;}}`,
  });

  const handlePrintWithAttachment = async () => {
    const ver = policy?.policyVersions[idVersion];
    if (ver?.attachment?.mimetype === 'application/pdf') {
      try {
        Dialog.loading({ title: t('policy:versionShow.swal.preparingPdf'), message: t('policy:versionShow.swal.extractingContent') });
        const urlToUse = currentAttachmentUrl || ver.attachment.url;
        const fullUrl = urlToUse.startsWith('http') ? urlToUse : `${apiClient.defaults.baseURL}${urlToUse}`;
        const response = await fetch(fullUrl);
        const arrayBuffer = await response.arrayBuffer();
        const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
        const SCALE = 150 / 72;
        const pages = [];
        for (let n = 1; n <= pdf.numPages; n++) {
          const page = await pdf.getPage(n);
          const viewport = page.getViewport({ scale: SCALE });
          const canvas = document.createElement('canvas');
          canvas.width = Math.ceil(viewport.width);
          canvas.height = Math.ceil(viewport.height);
          await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
          pages.push(canvas.toDataURL('image/png'));
        }
        Dialog.close();
        setPdfPages(pages);
        setShowCombinedContent(true);
        setTimeout(() => { printCombined(); setTimeout(() => { setShowCombinedContent(false); setPdfPages([]); }, 1000); }, 300);
      } catch { Dialog.close(); handlePrint(); }
    } else { handlePrint(); }
  };

  const handleSaveTranscription = () => {
    Dialog.alert({ title: t('policy:versionShow.swal.successTitle'), message: t('policy:versionShow.swal.transcriptionSaved'), icon: 'success', confirmLabel: 'OK' })
      .then(() => { setShowTranscriptionModal(false); setTranscribedText(''); });
  };

  // ── File helpers ──────────────────────────────────────────────────
  const getFileIcon = (mimetype) => {
    if (mimetype === 'application/pdf') return <PdfIcon size={24} fill={'var(--vo-danger)'} />;
    if (mimetype === 'application/msword' || mimetype === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') return <DocIcon size={24} fill={'var(--vo-info)'} />;
    return <AttachFileIcon size={24} fill={'var(--vo-slate)'} />;
  };

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 Bytes';
    const k = 1024, sizes = ['Bytes', 'KB', 'MB', 'GB'], i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  // ── Attachment Viewer ─────────────────────────────────────────────
  const AttachmentViewer = React.memo(({ attachment }) => {
    const [attachmentUrl, setAttachmentUrl] = useState(null);
    const [isRegenerating, setIsRegenerating] = useState(false);
    const [iframeError, setIframeError] = useState(false);
    const iframeRef = React.useRef(null);

    React.useEffect(() => {
      const init = async () => {
        if (!attachment) return;
        try {
          const res = await regenerateAttachmentDownloadUrl(id, idVersion);
          if (res.data?.url) { setAttachmentUrl(res.data.url); setCurrentAttachmentUrl(res.data.url); }
          else { setAttachmentUrl(attachment.url); setCurrentAttachmentUrl(attachment.url); }
        } catch { setAttachmentUrl(attachment.url); setCurrentAttachmentUrl(attachment.url); }
      };
      init();
    }, [attachment]);

    if (!attachment) return null;

    const regenerateUrlAutomatically = async () => {
      try {
        setIsRegenerating(true);
        const res = await regenerateAttachmentDownloadUrl(id, idVersion);
        if (res.data?.url) { setAttachmentUrl(res.data.url); setCurrentAttachmentUrl(res.data.url); setIframeError(false); }
        else setIframeError(true);
      } catch { setIframeError(true); } finally { setIsRegenerating(false); }
    };

    const regenerateUrl = async () => {
      try {
        setIsRegenerating(true);
        const res = await regenerateAttachmentDownloadUrl(id, idVersion);
        if (res.data?.url) {
          setAttachmentUrl(res.data.url); setCurrentAttachmentUrl(res.data.url); setIframeError(false);
          Dialog.alert({ title: t('policy:versionShow.swal.successTitle'), message: res.data.message || t('policy:versionShow.swal.urlRegenSuccess'), icon: 'success' });
        }
      } catch { Dialog.alert({ title: t('policy:versionShow.swal.errorTitle'), message: t('policy:versionShow.swal.urlRegenError'), icon: 'error' }); } finally { setIsRegenerating(false); }
    };

    const handleIframeLoad = () => {
      setIframeError(false);
      setTimeout(() => {
        try {
          const iframe = iframeRef.current;
          if (iframe?.contentDocument) {
            const content = iframe.contentDocument.body?.innerText || '';
            if (['Access Denied','AccessDenied','Forbidden','403','Token has expired','SignatureDoesNotMatch','ExpiredToken','The provided token has expired'].some(s => content.includes(s))) {
              setIframeError(true); regenerateUrlAutomatically();
            }
          }
        } catch { /* CORS - normal */ }
      }, 1000);
    };

    return (
      <div>
        <AttachFileRow>
          {getFileIcon(attachment.mimetype)}
          <div>
            <AttachFileName>{attachment.originalName}</AttachFileName>
            <AttachFileMeta>{attachment.mimetype} · {formatFileSize(attachment.size)}</AttachFileMeta>
          </div>
        </AttachFileRow>

        {attachment.mimetype === 'application/pdf' && (
          <div style={{ padding: '0 0 0 0' }}>
            {iframeError && (
              <div style={{ margin: '16px 24px', padding: 16, backgroundColor: 'color-mix(in srgb, var(--vo-warning) 6%, transparent)', borderRadius: 8, border: '1px solid color-mix(in srgb, var(--vo-warning) 20%, transparent)' }}>
                <p style={{ margin: '0 0 8px', color: 'var(--vo-slate)', fontSize: 13, fontFamily: 'Lato' }}>{t('policy:versionShow.attachment.linkExpired')}</p>
                <ButtonForm variant="secondary" onClick={regenerateUrl} disabled={isRegenerating}>
                  {isRegenerating ? t('policy:versionShow.attachment.reloading') : t('policy:versionShow.attachment.reloadDocument')}
                </ButtonForm>
              </div>
            )}
            <div style={{ height: 600, borderTop: '1px solid var(--vo-cloud)' }}>
              <iframe
                ref={iframeRef}
                key={attachmentUrl}
                src={attachmentUrl || 'about:blank'}
                width="100%"
                height="100%"
                style={{ border: 'none', display: 'block' }}
                title={attachment.originalName}
                onLoad={handleIframeLoad}
                onError={() => { setIframeError(true); regenerateUrlAutomatically(); }}
              >
                <p>{t('policy:versionShow.attachment.iframeFallback')}<a href={attachmentUrl} target="_blank" rel="noreferrer">{t('policy:versionShow.attachment.iframeFallbackLink')}</a></p>
              </iframe>
            </div>
          </div>
        )}

        {attachment.mimetype !== 'application/pdf' && (
          <div style={{ padding: '16px 24px', color: 'var(--vo-slate)', fontFamily: 'Lato', fontSize: 13 }}>
            {t('policy:versionShow.attachment.noPreview')}
          </div>
        )}
      </div>
    );
  });
  AttachmentViewer.displayName = 'AttachmentViewer';

  // ═════════════════════════════════════════════════════════════════
  const ver = policy?.policyVersions?.[idVersion];
  const userId = user?.userId;

  // Stage done/active logic
  const authorDone  = ver && ver.status !== 'Iniciado';
  const reviewerDone = ver && ['Revisado', 'Aprovado', 'Publicado'].includes(ver.status);
  const approverDone = ver && ['Aprovado', 'Publicado'].includes(ver.status);
  const publisherDone = ver && ver.status === 'Publicado';

  const isAuthor   = ver && (ver.author?._id === userId   || ver.author?.id === userId);
  const isReviewer = ver && (ver.reviewer?._id === userId || ver.reviewer?.id === userId);
  const isApprover = ver && (ver.approver?._id === userId || ver.approver?.id === userId);

  const authorActive   = ver && !authorDone && isAuthor;
  const reviewerActive = ver && !reviewerDone && isReviewer && ver.status === 'Concluído';
  const approverActive = ver && !approverDone && isApprover && ver.status === 'Revisado';
  const publisherActive = ver && !publisherDone && isAuthor && ver.status === 'Aprovado';

  const statusBadge = ver ? getVersionStatusBadge(ver.status) : { color: 'var(--vo-slate)', bg: 'var(--vo-slate-10)' };

  // The stage the version is currently waiting on (independent of who is viewing), and since when:
  // the last recorded workflow move, else the date that opened this stage.
  const currentStage = ver && ({
    [POLICY_VERSION_STATUS.STARTED]: 'author',
    [POLICY_VERSION_STATUS.SUBMITTED]: 'reviewer',
    [POLICY_VERSION_STATUS.REVIEWED]: 'approver',
    [POLICY_VERSION_STATUS.APPROVED]: 'publisher',
  })[ver.status];
  const lastMove = ver?.statusHistory?.length ? ver.statusHistory[ver.statusHistory.length - 1] : null;
  const stageOpenedAt = ver && (
    (lastMove?.status === ver.status && lastMove.at) ||
    ({
      [POLICY_VERSION_STATUS.STARTED]: ver.createdAt,
      [POLICY_VERSION_STATUS.SUBMITTED]: ver.submittedDate,
      [POLICY_VERSION_STATUS.REVIEWED]: ver.reviewedDate,
      [POLICY_VERSION_STATUS.APPROVED]: ver.approvedDate,
    })[ver.status]
  );
  const PendingSince = ({ stage }) => (currentStage === stage && stageOpenedAt
    ? <StepDate>{t('policy:versionShow.pipeline.pendingSince', { date: formatDateLongWithTime(stageOpenedAt) })}</StepDate>
    : null);

  return (
    <>
      <Container>
      <PageWrap>
        {isLoading && <PageLoaderShow />}

        {!isLoading && policy && ver && (
          <div ref={componentRef}>
            {/* ── Print-only cover page (ported from the working combinedRef block below) ── */}
            <div className="document-cover" style={{ display: 'none' }}>
              {brandingLogoUrl ? (
                <div style={{ marginBottom: 15 }}><img src={brandingLogoUrl} alt={tenant?.name} style={{ maxWidth: 320, maxHeight: 180, objectFit: 'contain' }} /></div>
              ) : (
                <h1 style={{ fontWeight: 600, color: 'var(--color-brand)', marginBottom: 10, fontSize: '28pt' }}>{tenant?.name}</h1>
              )}
              <div style={{ width: '60%', height: 2, backgroundColor: 'var(--color-brand)', marginBottom: 30, marginTop: 10 }} />
              <h2 style={{ fontWeight: 700, color: 'var(--vo-ink)', marginBottom: 20, fontSize: '32pt', lineHeight: 1.3 }}>{policy?.name}</h2>
              <h3 style={{ color: 'var(--vo-slate)', marginBottom: 40, fontSize: '14pt', textTransform: 'uppercase', letterSpacing: '2px' }}>{policy?.classification}</h3>
              <div style={{ width: '40%', height: 1, backgroundColor: 'var(--vo-line)', marginBottom: 40, marginTop: 20 }} />
              <div style={{ marginBottom: 40 }}>
                <p style={{ color: 'var(--vo-ink-2)', marginBottom: 10, fontSize: '12pt' }}><strong>{t('policy:versionShow.versionLabel')}:</strong> {ver?.version}</p>
                <p style={{ color: 'var(--vo-ink-2)', marginBottom: 10, fontSize: '12pt' }}><strong>{t('policy:versionShow.publishedDateLabel')}:</strong> {ver?.publishedDate ? formatDateToDateBRPolish(ver.publishedDate) : t('policy:versionShow.notPublished')}</p>
                <p style={{ color: 'var(--vo-ink-2)', fontSize: '12pt' }}><strong>{t('policy:versionShow.authorLabel')}:</strong> {ver?.author?.name || 'N/A'}</p>
              </div>
              <div style={{ position: 'absolute', bottom: 40, width: '100%', textAlign: 'center' }}>
                <p style={{ color: 'var(--vo-steel)', fontSize: '10pt', margin: 0 }}>{t('policy:versionShow.officialDocument')}</p>
                <p style={{ color: 'var(--vo-steel)', fontSize: '9pt', marginTop: 10, margin: 0 }}>{tenant?.corporateName || tenant?.name || ''}</p>
              </div>
            </div>

            {/* ── Print-only approval-workflow summary page (page 2) ── */}
            <div className="document-summary" style={{ display: 'none' }}>
              <h2 style={{ fontWeight: 700, color: 'var(--vo-ink)', fontSize: '22pt', marginBottom: 6 }}>{policy?.name}</h2>
              <p style={{ color: 'var(--vo-slate)', fontSize: '11pt', marginBottom: 30 }}>{t('policy:versionShow.versionLabel')} {ver?.version}</p>

              <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 36, fontSize: '11pt' }}>
                <tbody>
                  <tr>
                    <td style={{ padding: '6px 0', color: 'var(--vo-slate)', width: '40%' }}>{t('policy:versionShow.classificationLabel')}</td>
                    <td style={{ padding: '6px 0', color: 'var(--vo-ink)', fontWeight: 600 }}>{policy?.classification || '—'}</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '6px 0', color: 'var(--vo-slate)' }}>{t('policy:versionShow.authorLabel')}</td>
                    <td style={{ padding: '6px 0', color: 'var(--vo-ink)', fontWeight: 600 }}>{ver?.author?.name || '—'}</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '6px 0', color: 'var(--vo-slate)' }}>{t('policy:versionShow.publishedDateLabel')}</td>
                    <td style={{ padding: '6px 0', color: 'var(--vo-ink)', fontWeight: 600 }}>
                      {ver?.publishedDate ? formatDateToDateBRPolish(ver.publishedDate) : t('policy:versionShow.notPublished')}
                    </td>
                  </tr>
                </tbody>
              </table>

              <h3 style={{ fontWeight: 700, color: 'var(--color-brand)', fontSize: '13pt', marginBottom: 14, borderBottom: '2px solid var(--color-brand)', paddingBottom: 6 }}>
                {t('policy:versionShow.pipeline.title')}
              </h3>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10.5pt' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', padding: '8px 6px', borderBottom: '1px solid var(--vo-line)', color: 'var(--vo-slate)' }}>{t('policy:versionShow.table.stage')}</th>
                    <th style={{ textAlign: 'left', padding: '8px 6px', borderBottom: '1px solid var(--vo-line)', color: 'var(--vo-slate)' }}>{t('policy:versionShow.table.status')}</th>
                    <th style={{ textAlign: 'left', padding: '8px 6px', borderBottom: '1px solid var(--vo-line)', color: 'var(--vo-slate)' }}>{t('policy:versionShow.table.date')}</th>
                    <th style={{ textAlign: 'left', padding: '8px 6px', borderBottom: '1px solid var(--vo-line)', color: 'var(--vo-slate)' }}>{t('policy:versionShow.table.responsible')}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{ padding: '8px 6px', borderBottom: '1px solid var(--vo-cloud)' }}>{t('policy:versionShow.pipeline.stages.reviewer')}</td>
                    <td style={{ padding: '8px 6px', borderBottom: '1px solid var(--vo-cloud)' }}>{reviewerDone ? t('policy:versionShow.statusReviewed') : t('policy:versionShow.statusPending')}</td>
                    <td style={{ padding: '8px 6px', borderBottom: '1px solid var(--vo-cloud)' }}>{ver?.reviewedDate ? formatDateLongWithTime(ver.reviewedDate) : '—'}</td>
                    <td style={{ padding: '8px 6px', borderBottom: '1px solid var(--vo-cloud)' }}>{ver?.reviewer?.name || '—'}</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '8px 6px', borderBottom: '1px solid var(--vo-cloud)' }}>{t('policy:versionShow.pipeline.stages.approver')}</td>
                    <td style={{ padding: '8px 6px', borderBottom: '1px solid var(--vo-cloud)' }}>{approverDone ? t('policy:versionShow.statusApproved') : t('policy:versionShow.statusPending')}</td>
                    <td style={{ padding: '8px 6px', borderBottom: '1px solid var(--vo-cloud)' }}>{ver?.approvedDate ? formatDateLongWithTime(ver.approvedDate) : '—'}</td>
                    <td style={{ padding: '8px 6px', borderBottom: '1px solid var(--vo-cloud)' }}>{ver?.approver?.name || '—'}</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '8px 6px' }}>{t('policy:versionShow.pipeline.stages.publisher')}</td>
                    <td style={{ padding: '8px 6px' }}>{publisherDone ? t('policy:versionShow.statusPublished') : t('policy:versionShow.statusPending')}</td>
                    <td style={{ padding: '8px 6px' }}>{ver?.publishedDate ? formatDateLongWithTime(ver.publishedDate) : '—'}</td>
                    <td style={{ padding: '8px 6px' }}>{ver?.author?.name || '—'}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* ── Print-only document body (page 3) — mirrors DocCard's content, since DocCard itself is .no-print ── */}
            <div className="document-body" style={{ display: 'none' }}>
              <h1 style={{ textAlign: 'center', marginBottom: 24, fontSize: '18pt', fontWeight: 700, color: 'var(--vo-ink)' }}>{policy?.name}</h1>
              {ver?.attachment ? (
                <p style={{ fontSize: '11pt', color: 'var(--vo-ink-2)', textAlign: 'center' }}>{t('policy:versionShow.attachment.noPreview')}</p>
              ) : (
                <div className="ql-editor" style={{ fontSize: '12pt', lineHeight: 1.6, color: 'var(--vo-ink)' }} dangerouslySetInnerHTML={{ __html: ver?.policyText || '' }} />
              )}
            </div>

            {/* ── Page Header ──────────────────────────────────── */}
            <PageHeader
              className="no-print"
              title={policy.name}
              segments={[
                { label: t('common:breadcrumbs.compliance') },
                { label: t('policy:page.title'), route: '/policies' },
                { label: policy.name, route: `/policies/show/${id}` },
                { label: `${t('policy:versionShow.versionLabel')} ${ver.version}` },
              ]}
              actions={
                <HeaderActions>
                  {canExportPDF() && (
                    <OutlineBtn type="button" onClick={handlePrintWithAttachment}>
                      <IconPrint />
                      {t('policy:versionShow.exportPdf')}
                    </OutlineBtn>
                  )}
                  <PrimaryBtn type="button" onClick={() => navigate(`/policies/${id}/versions/add`)}>
                    <PlusSvg />
                    {t('policy:versions.titleAdd')}
                  </PrimaryBtn>
                </HeaderActions>
              }
            />

            {/* ── Card 1 · Policy Info ─────────────────────────── */}
            <InfoCard className="no-print">
              <InfoHeader>
                <GradientIcon><IconDoc /></GradientIcon>
                <InfoContent>
                  <InfoNameRow>
                    <PolicyName>{policy.name}</PolicyName>
                    <VersionBadge>v{ver.version}</VersionBadge>
                  </InfoNameRow>
                  <MetaRow>
                    {policy.classification && (
                      <MetaItem>{t('policy:versionShow.classificationLabel')}: <strong>{policy.classification}</strong></MetaItem>
                    )}
                    {ver.publishedDate && (
                      <MetaItem>{t('policy:versionShow.publishedLabel')}: <strong>{formatDateToDateBRPolish(ver.publishedDate)}</strong></MetaItem>
                    )}
                    {ver.author?.name && (
                      <MetaItem>{t('policy:versionShow.authorLabel')}: <strong>{ver.author.name}</strong></MetaItem>
                    )}
                  </MetaRow>
                </InfoContent>
              </InfoHeader>
            </InfoCard>

            {/* ── Card 2 · Approval Pipeline ───────────────────── */}
            <PipelineCard className="no-print">
              <PipelineHeader>
                <div>
                  <PipelineTitle>{t('policy:versionShow.pipeline.title')}</PipelineTitle>
                  <PipelineSubtitle>{policy.name} — <span style={{ fontWeight: 600, color: 'var(--color-brand)' }}>v{ver.version}</span></PipelineSubtitle>
                </div>
                <StatusBadge $color={statusBadge.color} $bg={statusBadge.bg}>{ver.status}</StatusBadge>
              </PipelineHeader>

              <PipelineGrid>
                {/* Step 1 · Autor */}
                <StepBox $done={authorDone} $active={authorActive}>
                  <StepTop>
                    <StepIconBox $done={authorDone} $active={authorActive}>
                      {authorDone
                        ? <IconCheck color="var(--vo-success)" size={14} />
                        : <IconPerson color={authorActive ? 'white' : 'var(--vo-steel)'} size={14} />}
                    </StepIconBox>
                    <div>
                      <StepLabel>{t('policy:versionShow.pipeline.stages.author')}</StepLabel>
                      <StepName>{ver.author?.name || '—'}</StepName>
                      {authorDone && ver.submittedDate && <StepDate $done>{t('policy:versionShow.pipeline.sentOn', { date: formatDateLongWithTime(ver.submittedDate) })}</StepDate>}
                      <PendingSince stage="author" />
                    </div>
                  </StepTop>
                  {authorActive && (
                    <div>
                      <StepNote>{t('policy:versionShow.pipeline.waitingDraft')}</StepNote>
                      <StepBtn type="button" $green onClick={() => updateStatus('Concluído')}>
                        <IconCheck />
                        {t('policy:versionShow.pipeline.markDone')}
                      </StepBtn>
                    </div>
                  )}
                  {!authorDone && !authorActive && (
                    <StepNote>{t('policy:versionShow.pipeline.waitingDraft')}</StepNote>
                  )}
                </StepBox>

                <PipelineArrow><IconArrow /></PipelineArrow>

                {/* Step 2 · Revisor */}
                <StepBox $done={reviewerDone} $active={reviewerActive}>
                  <StepTop>
                    <StepIconBox $done={reviewerDone} $active={reviewerActive}>
                      {reviewerDone
                        ? <IconCheck color="var(--vo-success)" size={14} />
                        : <IconPerson color={reviewerActive ? 'white' : 'var(--vo-steel)'} size={14} />}
                    </StepIconBox>
                    <div>
                      <StepLabel>{t('policy:versionShow.pipeline.stages.reviewer')}</StepLabel>
                      <StepName>{ver.reviewer?.name || '—'}</StepName>
                      {ver.reviewedDate && <StepDate $done>{formatDateLongWithTime(ver.reviewedDate)}</StepDate>}
                      <PendingSince stage="reviewer" />
                    </div>
                  </StepTop>
                  {reviewerActive && (
                    <div>
                      <StepNote>{t('policy:versionShow.pipeline.waitingReview')}</StepNote>
                      <StepBtns>
                        <StepBtn type="button" onClick={() => updateStatus('Revisado')}>
                          <IconCheck />
                          {t('policy:versionShow.pipeline.review')}
                        </StepBtn>
                        <StepBtn type="button" $danger onClick={() => handleReject('revisor')}>
                          <IconX />
                          {t('policy:versionShow.pipeline.reject')}
                        </StepBtn>
                      </StepBtns>
                    </div>
                  )}
                  {!reviewerDone && !reviewerActive && authorDone && (
                    <StepNote>{t('policy:versionShow.status.notReviewer')}</StepNote>
                  )}
                </StepBox>

                <PipelineArrow><IconArrow /></PipelineArrow>

                {/* Step 3 · Aprovador */}
                <StepBox $done={approverDone} $active={approverActive}>
                  <StepTop>
                    <StepIconBox $done={approverDone} $active={approverActive}>
                      {approverDone
                        ? <IconCheck color="var(--vo-success)" size={14} />
                        : <IconPerson color={approverActive ? 'white' : 'var(--vo-steel)'} size={14} />}
                    </StepIconBox>
                    <div>
                      <StepLabel>{t('policy:versionShow.pipeline.stages.approver')}</StepLabel>
                      <StepName>{ver.approver?.name || '—'}</StepName>
                      {ver.approvedDate && <StepDate $done>{formatDateLongWithTime(ver.approvedDate)}</StepDate>}
                      <PendingSince stage="approver" />
                    </div>
                  </StepTop>
                  {approverActive && (
                    <div>
                      <StepNote>{t('policy:versionShow.pipeline.waitingApproval')}</StepNote>
                      <StepBtns>
                        <StepBtn type="button" onClick={() => updateStatus('Aprovado')}>
                          <IconCheck />
                          {t('policy:versionShow.pipeline.approve')}
                        </StepBtn>
                        <StepBtn type="button" $danger onClick={() => handleReject('aprovador')}>
                          <IconX />
                          {t('policy:versionShow.pipeline.reject')}
                        </StepBtn>
                      </StepBtns>
                    </div>
                  )}
                  {!approverDone && !approverActive && reviewerDone && (
                    <StepNote>{t('policy:versionShow.status.notApprover')}</StepNote>
                  )}
                </StepBox>

                <PipelineArrow><IconArrow /></PipelineArrow>

                {/* Step 4 · Publicador */}
                <StepBox $done={publisherDone} $active={publisherActive}>
                  <StepTop>
                    <StepIconBox $done={publisherDone} $active={publisherActive}>
                      {publisherDone
                        ? <IconCheck color="var(--vo-success)" size={14} />
                        : <IconPublish color={publisherActive ? 'white' : 'var(--vo-steel)'} size={14} />}
                    </StepIconBox>
                    <div>
                      <StepLabel>{t('policy:versionShow.pipeline.stages.publisher')}</StepLabel>
                      <StepName>{ver.author?.name || '—'}</StepName>
                      {ver.publishedDate && <StepDate $done>{formatDateLongWithTime(ver.publishedDate)}</StepDate>}
                      <PendingSince stage="publisher" />
                    </div>
                  </StepTop>
                  {publisherActive && (
                    <div>
                      <StepNote>{t('policy:versionShow.pipeline.waitingPublication')}</StepNote>
                      <StepBtn type="button" $green onClick={() => updateStatus('Publicado')}>
                        <IconPublish />
                        {t('policy:versionShow.pipeline.publish')}
                      </StepBtn>
                    </div>
                  )}
                  {!publisherDone && !publisherActive && approverDone && (
                    <StepNote>{t('policy:versionShow.status.notPublisher')}</StepNote>
                  )}
                </StepBox>
              </PipelineGrid>
            </PipelineCard>

            {/* ── Card 3 · Accepts Counter ─────────────────────── */}
            {ver.publishedDate && (
              <AcceptsCard className="no-print">
                <AcceptsLeft>
                  <AcceptsIconBox><IconUsers /></AcceptsIconBox>
                  <div>
                    <AcceptsNum data-testid="policy-accepts-summary">
                      {acceptsSummary?.sent ?? 0} <AcceptsUnit>({t('policy:versionShow.accepts.sent')})</AcceptsUnit>
                      {' / '}
                      {acceptsSummary?.accepted ?? 0} <AcceptsUnit>({t('policy:versionShow.accepts.accepted')})</AcceptsUnit>
                    </AcceptsNum>
                    <AcceptsLabel>{t('policy:versionShow.accepts.count')}</AcceptsLabel>
                  </div>
                </AcceptsLeft>
                {policyAccepts > 0 ? (
                  <Button variant="outline" type="button" onClick={() => navigate(`/policies/show/${id}/versions/${idVersion}/accepts`)} sx={{ height: '36px', padding: '0 14px', fontSize: '12px' }}>
                    {t('policy:versionShow.accepts.view')}
                  </Button>
                ) : policyAccepts === 0 ? (
                  <Button variant="outline" type="button" onClick={() => mutation.mutate()} sx={{ height: '36px', padding: '0 14px', fontSize: '12px' }}>
                    {t('policy:versionShow.accepts.send')}
                  </Button>
                ) : null}
              </AcceptsCard>
            )}

            {/* ── Card 4 · Document Viewer ─────────────────────── */}
            <DocCard className="no-print">
              <DocHeader>
                <DocTitle>{t('policy:versionShow.document.title')}</DocTitle>
                <DocBtns>
                  <SmallOutlineBtn type="button" onClick={() => {
                    if (ver.attachment) {
                      window.open(currentAttachmentUrl || ver.attachment.url, '_blank');
                    } else {
                      handlePrintWithAttachment();
                    }
                  }}>
                    <IconDownload />
                    {t('policy:versionShow.document.download')}
                  </SmallOutlineBtn>
                </DocBtns>
              </DocHeader>

              {ver.attachment ? (
                <AttachmentViewer attachment={ver.attachment} />
              ) : (
                <DocContent>
                  <DocPaper>
                    <DocPaperTitle>{policy.name}</DocPaperTitle>
                    <DocPaperSub>
                      v{ver.version}
                      {ver.publishedDate && ` · ${t('policy:versionShow.publishedLabel')} ${formatDateToDateBRPolish(ver.publishedDate)}`}
                    </DocPaperSub>
                    <div
                      className="ql-snow ql-editor"
                      style={{ fontFamily: 'Lato', fontSize: 14, color: 'var(--vo-ink-2)', lineHeight: 1.7 }}
                      dangerouslySetInnerHTML={{ __html: ver.policyText || '' }}
                    />
                  </DocPaper>
                </DocContent>
              )}
            </DocCard>

            {/* ── Card 5 · Comments / History ──────────────────── */}
            {canViewApprovalHistory() && (
              <CommentsCard className="no-print">
                <CommentsToggle $open={commentsExpanded} onClick={() => setCommentsExpanded(v => !v)}>
                  <CommentsToggleLeft>
                    <IconChat />
                    <CommentsTitleText>{t('policy:versionShow.comments.title')}</CommentsTitleText>
                    {comments.length > 0 && <CommentsCount>{comments.length}</CommentsCount>}
                  </CommentsToggleLeft>
                  <ChevronIcon
                    $open={commentsExpanded}
                    width="16" height="16" viewBox="0 0 24 24"
                    fill="none" stroke="var(--vo-slate)" strokeWidth="2"
                    strokeLinecap="round" strokeLinejoin="round"
                  >
                    <polyline points="6 9 12 15 18 9"/>
                  </ChevronIcon>
                </CommentsToggle>

                {commentsExpanded && (
                  <>
                    <CommentsBody>
                      {comments.length > 0 ? (
                        [...comments]
                          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                          .map((comment, idx) => {
                            const style = getCommentStyle(comment);
                            const badgeLabel = comment.type === 'rejection'
                              ? `${t('policy:versionShow.comments.rejectionLabel')} — ${comment.authorRole}`
                              : comment.authorRole;
                            return (
                              <CommentItem key={comment.id || idx}>
                                <CommentIconBox $bg={style.iconBg}>
                                  {style.icon === 'x'
                                    ? <IconX color={style.iconColor} size={14} />
                                    : style.icon === 'check'
                                    ? <IconCheck color={style.iconColor} size={14} />
                                    : <IconChat />}
                                </CommentIconBox>
                                <CommentContent>
                                  <CommentMeta>
                                    <CommentAuthor>{comment.authorName}</CommentAuthor>
                                    <CommentBadge $color={style.badgeColor} $bg={style.badgeBg}>{badgeLabel}</CommentBadge>
                                    <CommentDate>{formatDateTimeToDateTimeBR(comment.createdAt)}</CommentDate>
                                  </CommentMeta>
                                  <CommentText>{comment.content}</CommentText>
                                </CommentContent>
                              </CommentItem>
                            );
                          })
                      ) : (
                        <EmptyNote>{t('policy:versionShow.comments.empty')}</EmptyNote>
                      )}
                    </CommentsBody>

                    {canAddComment() && (
                      <CommentsFooter>
                        <AddCommentBtn type="button" onClick={(e) => { e.stopPropagation(); handleAddComment(); }}>
                          <PlusSvg />
                          {t('policy:versionShow.comments.add')}
                        </AddCommentBtn>
                      </CommentsFooter>
                    )}
                  </>
                )}
              </CommentsCard>
            )}
          </div>
        )}
      </PageWrap>
      </Container>

      {/* ── Hidden combined print content ─────────────────────────── */}
      {showCombinedContent && (
        <div style={{ display: 'none' }}>
          <div ref={combinedRef}>
            <div style={{ display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',height:'100vh',pageBreakAfter:'always',textAlign:'center',padding:'5px 40px 40px',border:'2px solid var(--color-brand)',borderRadius:8,margin:20 }}>
              {brandingLogoUrl ? (
                <div style={{ marginBottom:15 }}><img src={brandingLogoUrl} alt={tenant?.name} style={{ maxWidth:320,maxHeight:180,objectFit:'contain' }} /></div>
              ) : (
                <h1 style={{ fontWeight:600,color:'var(--color-brand)',marginBottom:10,fontSize:'28pt' }}>{tenant?.name}</h1>
              )}
              <div style={{ width:'60%',height:2,backgroundColor:'var(--color-brand)',marginBottom:30,marginTop:10 }} />
              <h2 style={{ fontWeight:700,color:'var(--vo-ink)',marginBottom:20,fontSize:'32pt',lineHeight:1.3 }}>{policy?.name}</h2>
              <h3 style={{ color:'var(--vo-slate)',marginBottom:40,fontSize:'14pt',textTransform:'uppercase',letterSpacing:'2px' }}>{policy?.classification}</h3>
              <div style={{ width:'40%',height:1,backgroundColor:'var(--vo-line)',marginBottom:40,marginTop:20 }} />
              <div style={{ marginBottom:40 }}>
                <p style={{ color:'var(--vo-ink-2)',marginBottom:10,fontSize:'12pt' }}><strong>{t('policy:versionShow.versionLabel')}:</strong> {ver?.version}</p>
                <p style={{ color:'var(--vo-ink-2)',marginBottom:10,fontSize:'12pt' }}><strong>{t('policy:versionShow.publishedDateLabel')}:</strong> {ver?.publishedDate ? formatDateToDateBRPolish(ver.publishedDate) : t('policy:versionShow.notPublished')}</p>
                <p style={{ color:'var(--vo-ink-2)',fontSize:'12pt' }}><strong>{t('policy:versionShow.authorLabel')}:</strong> {ver?.author?.name || 'N/A'}</p>
              </div>
              <div style={{ position:'absolute',bottom:40,width:'100%',textAlign:'center' }}>
                <p style={{ color:'var(--vo-steel)',fontSize:'10pt',margin:0 }}>{t('policy:versionShow.officialDocument')}</p>
                <p style={{ color:'var(--vo-steel)',fontSize:'9pt',marginTop:10,margin:0 }}>{tenant?.corporateName || tenant?.name || ''}</p>
              </div>
            </div>
            <div style={{ pageBreakBefore:'always',paddingTop:'20mm',marginBottom:40 }}>
              <h1 style={{ textAlign:'center',marginBottom:30,fontSize:24,fontWeight:'bold' }}>{policy?.name}</h1>
              <div dangerouslySetInnerHTML={{ __html: ver?.policyText || '' }} />
            </div>
            <div className="page-break" />
            {pdfPages.length > 0 && (
              <div style={{ marginTop:20,padding:'10px 0' }}>
                {pdfPages.map((src, i) => (
                  <div key={i} style={{ pageBreakBefore: i > 0 ? 'always' : 'auto', marginBottom:10 }}>
                    <img className="pdf-page-img" src={src} alt={`Página ${i + 1}`} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Transcription Modal ────────────────────────────────────── */}
      <Dialog.custom
        open={showTranscriptionModal}
        onClose={() => setShowTranscriptionModal(false)}
        title={`${t('policy:versionShow.transcription.title')} - ${ver?.attachment?.originalName || ''}`}
        actions={
          <div style={{ display:'flex',justifyContent:'flex-end',gap:8 }}>
            <Button variant="outline" onClick={() => setShowTranscriptionModal(false)} sx={{ height: '44px', padding: '0 20px' }}>{t('common:buttons.cancel')}</Button>
            <ButtonForm onClick={handleSaveTranscription} disabled={!transcribedText.trim()}>{t('policy:versionShow.transcription.save')}</ButtonForm>
          </div>
        }
      >
        <p style={{ fontSize:'0.875rem',color:'var(--vo-slate)',marginBottom:16 }}>{t('policy:versionShow.transcription.description')}</p>
        <TextField multiline rows={20} value={transcribedText} onChange={(e) => setTranscribedText(e.target.value)} placeholder={t('policy:versionShow.transcription.placeholder')} fullWidth />
      </Dialog.custom>
    </>
  );
};
